<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="a"
                :pages="[
                    {
                        name: $tc('messages.intervention_records', 2),
                        href: null,
                    },
                ]"
            />
        </template>

        <form :action="meta.links.index" method="GET" @submit.prevent="filter">
            <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-12">
                <div class="sm:col-span-2">
                    <div class="">
                        <FormLabel :value="$tc('messages.groups', 1)" for="group_id" />
                        <FormSelect
                            id="group_id"
                            v-model="form.group_id"
                            :options="options.groups"
                            class="mt-1 w-full"
                        />
                        <FormError :value="form.errors.group_id" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <div class="">
                        <FormLabel :value="$tc('messages.students', 1)" for="student_id" />
                        <FormSelect
                            :null-option-text="
                                form.group_id
                                    ? $t('messages.select')
                                    : $t('messages.select_X', { X: $tc('messages.groups', 1) })
                            "
                            id="student_id"
                            v-model="form.student_id"
                            :options="selects.students"
                            class="mt-1 w-full"
                        />
                        <FormError :value="form.errors.student_id" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2" v-show="options.school_years.length > 1">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.school_year')" for="school_year_id" />
                        <FormSelect
                            required
                            id="school_year_id"
                            v-model="form.school_year_id"
                            :options="options.school_years"
                            class="mt-1 w-full"
                            :null-option="false"
                        />
                        <FormError :value="form.errors.school_year_id" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <div class="mt-1">
                        <FormLabel :value="$tc('messages.external_entities', 1)" for="external_entity_id" />
                        <FormSelect
                            id="external_entity_id"
                            v-model="form.external_entity_id"
                            :options="options.external_entities"
                            class="mt-1 w-full"
                        />
                        <FormError :value="form.errors.external_entity_id" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <label for="school_year_id" class="block text-sm font-medium leading-5 text-gray-700">
                        {{ $t('messages.has_links') }}
                    </label>
                    <div class="mt-1 rounded-md">
                        <input
                            id="has_links"
                            v-model="form.has_links"
                            name="has_links"
                            :true-value="1"
                            :false-value="0"
                            type="checkbox"
                            class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                        />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <label class="block text-sm font-medium leading-5 text-gray-700"> &nbsp; </label>
                    <div class="flex justify-end space-x-2">
                        <SubmitFormButton
                            :button-text="$t('messages.filter')"
                            :processing="form.processing"
                            is-filter
                        />
                    </div>
                </div>
            </div>
        </form>

        <div class="flex flex-col mt-5">
            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                <div
                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                >
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead>
                            <tr>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.name') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.date') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $tc('messages.channels', 1) }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.intervention_assessment') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.type') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    <span class="sr-only">{{ $t('messages.actions') }}</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                v-for="(intervention, index) in interventions.data"
                                :key="intervention.id"
                            >
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    <InertiaLink :href="intervention.links.student_show" class="text-indigo-600">
                                        {{ intervention.student.full_name }}
                                    </InertiaLink>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ intervention.date_display }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ intervention.entity_name }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ intervention.channel_string }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    <div class="flex items-center space-x-3 lg:pl-2">
                                        <div
                                            class="shrink-0 w-2.5 h-2.5 rounded-full"
                                            :class="intervention.assessment_level_color"
                                        ></div>

                                        <span>
                                            {{ intervention.assessment_level_string }}
                                        </span>
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-normal text-sm leading-5 text-gray-500">
                                    {{ intervention.type_string }}
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500 text-right flex justify-end space-x-1"
                                >
                                    <InertiaSquareLinkButton action="show" :href="intervention.links.show" />
                                    <InertiaSquareLinkButton action="edit" :href="intervention.links.edit" />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <template v-slot:pagination>
            <InertiaPagination :paginator="interventions" />
        </template>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import InertiaPagination from '@/Shared/InertiaPagination.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormError from '@/components/Forms/FormError.vue';
import axios from 'axios';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';
import InertiaSquareLinkButton from '@/components/InertiaSquareLinkButton.vue';

export default {
    components: {
        InertiaSquareLinkButton,
        SubmitFormButton,
        PanelPage,
        InertiaPagination,
        FormSelect,
        FormLabel,
        FormError,
    },
    layout: Layout,
    name: 'Index',
    props: ['meta', 'interventions', 'options', 'filters', 'student'],
    data() {
        return {
            form: this.$inertia.form({
                group_id: this.filters.group_id,
                student_id: this.filters.student_id,
                school_year_id: this.filters.school_year_id,
                external_entity_id: this.filters.external_entity_id,
                has_links: this.filters.has_links,
            }),
            selects: {
                students: this.options.students,
            },
        };
    },
    methods: {
        filter() {
            this.form.get(this.meta.links.index);
        },
        fetchStudents() {
            axios.get(this.route('select.group_students.show', { group: this.form.group_id })).then((response) => {
                this.form.student_id = null;
                this.selects.students = response.data.items;
            });
        },
    },
    watch: {
        'form.group_id': {
            handler(newVal, oldVal) {
                if (newVal !== oldVal) {
                    if (newVal) {
                        this.fetchStudents();
                    } else {
                        this.form.student_id = null;
                        this.selects.students = [];
                    }
                }
            },
            deep: true,
        },
    },
};
</script>
